import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Col, Divider, Row, Skeleton } from 'antd'
import { Formik } from 'formik'
import * as Yup from 'yup'

import {
  customExpenseType,
  deleteCustomExpenseType,
  editCustomExpenseType,
} from 'core/application/adminDashboard/benefitDetails/design/perks/productClass/customExpenseType'
import {
  PerksCategoriesWithExpenseResponse,
  getCategoriesWithExpenseTypes,
} from 'core/application/adminDashboard/benefitDetails/design/perks/productClass/getCategoriesWithExpenseTypes'
import selectEligibleCategories, {
  SelectEligibleCategoriesRequest,
} from 'core/application/adminDashboard/benefitDetails/design/perks/productClass/selectEligibleCategories'

import Checkbox from 'common/components/dataEntry/components/Checkbox'
import Input from 'common/components/dataEntry/components/Input'
import FormikCheckboxGroupField from 'common/components/dataEntry/formik/FormikCheckboxGroupField'
import Alert from 'common/components/feedback/Alert'
import Button from 'common/components/general/Button'
import Icon from 'common/components/general/Icon'

import useCommandRequest from 'common/hooks/useCommandRequest'
import useQueryRequest from 'common/hooks/useQueryRequest'

import DeleteConfirmModal from '../../../../../../../common/components/dataDisplay/DeleteConfirmModal'
import { BaseComponentProps } from '../../../../../common/productClassWizardModal/Common'

import './styles.less'

export type SetEligibleExpensesProps = {
  id: string
  eligibleCategoryIds: number[]
}

export default (props: BaseComponentProps<SetEligibleExpensesProps>) => {
  const { t: translate } = useTranslation()

  const [showEditCustomExpenseType, setEditShowCustomExpenseType] = useState<string | number>(0)
  const [showAddCustomExpenseType, setShowAddCustomExpenseType] = useState<string | number>(0)
  const [customExpenseTypeValue, setCustomExpenseTypeValue] = useState('')
  const [deleteCustomExpense, setDeleteCustomExpense] = useState(null)
  const { bindSubmitFormHandler } = props
  const [categories, isCategoryLoading, , reloadCategories] = useQueryRequest<PerksCategoriesWithExpenseResponse[]>(
    null,
    () => getCategoriesWithExpenseTypes(props.state.id, props.planPeriodId),
  )

  const [sendCommand, isCommandLoading] = useCommandRequest()
  const [initialValues] = useState<SelectEligibleCategoriesRequest>({
    eligibleCategoryIds: props.state.eligibleCategoryIds,
    productClassId: props.state.id,
  })

  const onSubmit = async (values: any) => {
    const request = {
      ...values,
      productClassId: props.state.id,
    }

    if (values.eligibleCategoryIds.length > 0) {
      const result = await sendCommand(() => selectEligibleCategories(request))

      if (result.completed) {
        props.goNext()
      } else {
        props.setIsSubmiting(false)
      }
    } else {
      props.setIsSubmiting(false)
    }
  }

  const isValidForm = (eligibleCategoryIds: any, selectedCategories: PerksCategoriesWithExpenseResponse[]) => {
    let isValid = true
    selectedCategories.forEach((x) => {
      const hasSelected = x.expenseTypes.some((x) => eligibleCategoryIds.includes(x.id))
      if (!hasSelected) {
        isValid = false
      }
    })
    return isValid
  }

  const handleSaveCustomExpenseType = async (categoryId, name, formikValue, formik) => {
    const result = await sendCommand(() => customExpenseType(name, categoryId, props.state.id))
    if (result.completed) {
      reloadCategories().then((currentCategories) => {
        const eligibleCategoryIds = [...formikValue, result.data]
        formik.setFieldValue('eligibleCategoryIds', eligibleCategoryIds)
        setShowAddCustomExpenseType(0)
        setCustomExpenseTypeValue('')
        props.setIsFormValid(isValidForm(eligibleCategoryIds, currentCategories))
      })
    }
  }

  const handleEditCustomExpenseType = async (categoryId, name, expenseTypeId) => {
    const result = await sendCommand(() => editCustomExpenseType(name, categoryId, expenseTypeId))
    if (result.completed) {
      reloadCategories().then(() => {
        setEditShowCustomExpenseType(0)
        setCustomExpenseTypeValue('')
      })
    }
  }

  const handleDeleteCustomExpenseType = async (expenseTypeId, formikValue, formik) => {
    const result = await sendCommand(() => deleteCustomExpenseType(props.state.id, expenseTypeId))
    if (result.completed) {
      await reloadCategories().then((currentCategories) => {
        const eligibleCategoryIds = formikValue.filter((x) => x !== expenseTypeId)
        setDeleteCustomExpense(null)
        formik.setFieldValue('eligibleCategoryIds', eligibleCategoryIds)
        props.setIsFormValid(isValidForm(eligibleCategoryIds, currentCategories))
      })
    }
  }

  return (
    categories && (
      <Formik
        enableReinitialize={true}
        validationSchema={Yup.object({})}
        validateOnMount={true}
        initialValues={initialValues}
        onSubmit={onSubmit}
        validate={(values) => {
          const isValid = isValidForm(values.eligibleCategoryIds, categories)
          props.setHasDirtyData(true)
          props.setIsFormValid(isValid)
          return {}
        }}
      >
        {(formik) => {
          const { handleSubmit, submitForm } = formik
          bindSubmitFormHandler(submitForm)
          return (
            <form autoComplete="off" onSubmit={handleSubmit} className="select_class">
              <Skeleton loading={!categories}>
                {categories && (
                  <>
                    <Row>
                      <Col span={20}>
                        <h5 className="select_class_info mb-24">
                          {translate('admin-dashboard.benefit-details.class-design.perks-select-class.header')}
                        </h5>
                      </Col>
                    </Row>
                    <Alert
                      message=""
                      description={translate('admin-dashboard.benefit-details.class-design.perks-select-class.alert')}
                      type="info"
                      showIcon
                      icon={<Icon className="far fa-info-circle" />}
                      className="mb-16"
                    />

                    <p className={'color-gray-8 semi-bold'}>
                      {`${translate(
                        'admin-dashboard.benefit-details.class-design.perks-select-class.total-categories',
                      )}: ${categories.length}`}
                    </p>
                    <div className={'categories-wrapper'} style={{ width: '100%' }}>
                      <FormikCheckboxGroupField name="eligibleCategoryIds">
                        {categories.map((item, index) => (
                          <div className={'single-category-wrapper'}>
                            <div className={'color-gray-9 semi-bold mb-16'}>
                              {item.name}
                              <span className={'category-counter'}>{`(${index + 1}/${categories.length})`}</span>
                            </div>
                            {item.expenseTypes.map((expenseType) => (
                              <Row>
                                {showEditCustomExpenseType !== 0 && showEditCustomExpenseType === expenseType.id ? (
                                  <Col span={24}>
                                    <Row>
                                      <Col span={1}>
                                        <Checkbox value={expenseType.id} key={expenseType.id}></Checkbox>
                                      </Col>
                                      <Col span={14}>
                                        <Input
                                          onPressEnter={(e) => {
                                            e.stopPropagation()
                                            e.preventDefault()
                                          }}
                                          style={{ width: '100%' }}
                                          defaultValue={expenseType.name}
                                          autoFocus
                                          onChange={(e) => setCustomExpenseTypeValue(e.target.value)}
                                        />
                                      </Col>
                                      <Col span={5}>
                                        <Button
                                          type="primary"
                                          icon="far fa-check"
                                          className="ml-8"
                                          loading={isCategoryLoading}
                                          onClick={() =>
                                            handleEditCustomExpenseType(item.id, customExpenseTypeValue, expenseType.id)
                                          }
                                        >
                                          Submit
                                        </Button>
                                      </Col>
                                      <Col span={4}>
                                        <Button
                                          type="default"
                                          className="ml-8"
                                          onClick={() => {
                                            setEditShowCustomExpenseType('')
                                          }}
                                        >
                                          Cancel
                                        </Button>
                                      </Col>
                                    </Row>
                                  </Col>
                                ) : (
                                  <>
                                    <Col span={22}>
                                      <Checkbox value={expenseType.id} key={expenseType.id}>
                                        {expenseType.name}
                                      </Checkbox>
                                    </Col>
                                    {expenseType.isCustom &&
                                      formik.values?.eligibleCategoryIds?.find((i) => i === expenseType.id) && (
                                        <Col span={2}>
                                          <Row>
                                            <Col>
                                              <Icon
                                                className="fas fa-pencil pointer"
                                                onClick={() => setEditShowCustomExpenseType(expenseType.id)}
                                              />
                                            </Col>
                                            <Col>
                                              <Divider orientation="center" type="vertical" />
                                            </Col>
                                            <Col>
                                              <Icon
                                                className="color-red-6 fa fa-trash pointer"
                                                onClick={() => setDeleteCustomExpense(expenseType)}
                                              />
                                            </Col>
                                          </Row>
                                        </Col>
                                      )}
                                  </>
                                )}
                              </Row>
                            ))}
                            <>
                              {showAddCustomExpenseType !== 0 && showAddCustomExpenseType === item.id && (
                                <Row>
                                  <Col span={1}>
                                    <Checkbox></Checkbox>
                                  </Col>
                                  <Col span={14}>
                                    <Input
                                      onPressEnter={(e) => {
                                        e.stopPropagation()
                                        e.preventDefault()
                                      }}
                                      style={{ width: '100%' }}
                                      defaultValue={customExpenseTypeValue}
                                      autoFocus
                                      onChange={(e) => setCustomExpenseTypeValue(e.target.value)}
                                    />
                                  </Col>
                                  <Col span={5}>
                                    <Button
                                      type="primary"
                                      icon="far fa-check"
                                      className="ml-8"
                                      loading={isCategoryLoading}
                                      onClick={() =>
                                        handleSaveCustomExpenseType(
                                          item.id,
                                          customExpenseTypeValue,
                                          formik.values?.eligibleCategoryIds,
                                          formik,
                                        )
                                      }
                                    >
                                      Submit
                                    </Button>
                                  </Col>
                                  <Col span={4}>
                                    <Button
                                      type="default"
                                      className="ml-8"
                                      onClick={() => setShowAddCustomExpenseType(0)}
                                    >
                                      Cancel
                                    </Button>
                                  </Col>
                                </Row>
                              )}
                              <Row className="mt-8">
                                <Col span={24}>
                                  <h6>Can't find what you are looking for?</h6>
                                </Col>
                                <Col span={24}>
                                  <Button
                                    type="primary"
                                    ghost
                                    icon="far fa-plus"
                                    disabled={showAddCustomExpenseType === item.id}
                                    onClick={() => setShowAddCustomExpenseType(item.id)}
                                  >
                                    Add a custom category
                                  </Button>
                                </Col>
                              </Row>
                            </>
                          </div>
                        ))}
                      </FormikCheckboxGroupField>
                    </div>
                  </>
                )}
              </Skeleton>
              <DeleteConfirmModal
                title={`Deleting '${deleteCustomExpense?.name}' ...`}
                content={'Are you sure you want to delete this custom category? This action cannot be undone.'}
                onDelete={async () =>
                  handleDeleteCustomExpenseType(deleteCustomExpense.id, formik.values?.eligibleCategoryIds, formik)
                }
                onCancel={() => {
                  setDeleteCustomExpense(null)
                }}
                deleteButtonLoading={isCommandLoading}
                visible={!!deleteCustomExpense}
              />
            </form>
          )
        }}
      </Formik>
    )
  )
}
